import { useEffect, useState, useCallback } from "react";
import { FaHistory, FaSyncAlt } from "react-icons/fa";

import "./Dashboard.css";

const API_URL = process.env.REACT_APP_API_URL;

const LOG_LIST_ENDPOINT = `${API_URL}/portal/list`;

function getTodayDateString() {

    const now = new Date();

    const year = now.getFullYear();

    const month = String(now.getMonth() + 1).padStart(2, "0");

    const day = String(now.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;

}

function formatDateTime(value) {

    if (!value) {
        return "-";
    }

    return new Date(value).toLocaleString();

}

function TransactionLog() {

    const [logs, setLogs] = useState([]);

    const [logsLoading, setLogsLoading] = useState(true);

    const [logsError, setLogsError] = useState(null);

    const [statusFilter, setStatusFilter] = useState("all"); // all | success | failed

    const [fromDate, setFromDate] = useState(getTodayDateString());

    const [toDate, setToDate] = useState(getTodayDateString());

    // Always starts at 100 on page load - matches the backend's own
    // default. Changing the dropdown re-fetches with the new value.
    const [logLimit, setLogLimit] = useState(100);

    const fetchLogs = useCallback(async (from, to, limit) => {

        setLogsLoading(true);
        setLogsError(null);

        try {

            const params = new URLSearchParams();

            if (from) {
                params.append("from_date", from);
            }

            if (to) {
                params.append("to_date", to);
            }

            params.append("limit", limit || 100);

            const qs = params.toString();

            const url = qs ? `${LOG_LIST_ENDPOINT}?${qs}` : LOG_LIST_ENDPOINT;

            const res = await fetch(url, {
                headers: {
                    accept: "application/json"
                }
            });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            setLogs(Array.isArray(data) ? data : []);

        } catch (err) {

            console.error("Log list fetch failed:", err);

            setLogsError(err.message || "Unable to reach log list API");

            setLogs([]);

        } finally {

            setLogsLoading(false);

        }

    }, []);

    useEffect(() => {

        fetchLogs(fromDate, toDate, logLimit);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApplyFilters = () => {

        fetchLogs(fromDate, toDate, logLimit);

    };

    const handleResetFilters = () => {

        const today = getTodayDateString();

        setFromDate(today);

        setToDate(today);

        setStatusFilter("all");

        setLogLimit(100);

        fetchLogs(today, today, 100);

    };

    const filteredLogs = logs.filter((log) => {

        if (statusFilter === "success") {
            return log.status === true;
        }

        if (statusFilter === "failed") {
            return log.status === false;
        }

        return true;

    });

    return (

        <div className="dashboard">

            <div className="log-section">

                <div className="log-section-header">
                    <div>
                        <h3><FaHistory /> Transaction Log</h3>
                        <p className="log-section-note">Showing today's data by default</p>
                    </div>

                    <div className="log-filters">

                        <select
                            className="filter-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="success">Success</option>
                            <option value="failed">Failed</option>
                        </select>

                        <select
                            className="filter-select"
                            value={logLimit}
                            disabled={logsLoading}
                            onChange={(e) => {

                                const newLimit = Number(e.target.value);

                                setLogLimit(newLimit);

                                fetchLogs(fromDate, toDate, newLimit);

                            }}
                            title="Number of records to load"
                        >
                            <option value={100}>100 records</option>
                            <option value={200}>200 records</option>
                            <option value={300}>300 records</option>
                            <option value={500}>500 records</option>
                        </select>

                        <input
                            type="date"
                            className="filter-date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />

                        <span>to</span>

                        <input
                            type="date"
                            className="filter-date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />

                        <button className="filter-btn filter-apply" onClick={handleApplyFilters} disabled={logsLoading}>
                            Apply
                        </button>

                        <button className="filter-btn filter-reset" onClick={handleResetFilters} disabled={logsLoading}>
                            Reset
                        </button>

                    </div>

                </div>

                {logsLoading && (
                    <p className="empty-note"><FaSyncAlt className="spin" /> Loading transaction log...</p>
                )}

                {!logsLoading && logsError && (
                    <p className="log-table-error">{logsError}</p>
                )}

                {!logsLoading && !logsError && (

                    <div className="log-table-wrapper">

                        <table className="log-table">

                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>API Name</th>
                                    <th>Status</th>
                                    <th>Remarks</th>
                                    <th>Request Received</th>
                                </tr>
                            </thead>

                            <tbody>

                                {

                                    filteredLogs.length === 0 &&

                                    <tr>
                                        <td colSpan={5} className="log-table-message">
                                            No transaction log entries for this filter/date range.
                                        </td>
                                    </tr>

                                }

                                {

                                    filteredLogs.map((log) => (

                                        <tr key={log.id}>
                                            <td>{log.id}</td>
                                            <td>{log.api_name}</td>
                                            <td>
                                                <span className={`status-badge ${log.status ? "status-ok" : "status-down"}`}>
                                                    {log.status ? "ONLINE" : "OFFLINE"}
                                                </span>
                                            </td>
                                            <td>{log.remarks}</td>
                                            <td>{formatDateTime(log.created_at)}</td>
                                        </tr>

                                    ))

                                }

                            </tbody>

                        </table>

                    </div>

                )}

            </div>

        </div>

    );

}

export default TransactionLog;
